import { Accordion, AccordionDetails, AccordionSummary, Button, Menu, MenuItem } from '@material-ui/core';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCaretDown,
  faChevronDown,
  faCircleCheck,
  faCircleExclamation,
  faClock,
  faMinusCircle,
  faSpinner,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import React from 'react';
import styled from 'styled-components';
import { getCacheKey } from 'helpers/cache';
import { useCacheData } from 'hooks/useCacheData';
import CoursesSelectModel from 'models/CoursesSelectModel';
import { ICourse } from 'types/courses';
import { IRefIds } from 'constants/refs';
import HealthCourseSelect from './components/HealthCourseSelect';
import HealthCourseContent from './components/HealthCourseContent';
import PublishGateView from './components/PublishGateView';
import CoursePublish from './components/CoursePublish';
import usePublishGate from './hooks/usePublishGate';
import useCoursePublish from './hooks/useCoursePublish';
import PublishProgressBar from './components/PublishProgressBar';
import useHealthChecks, { OverallStatus } from './hooks/useHealthChecks';
import { ContentWrapper, ErrorMessage, ErrorWrapper } from './styles';

// Health dashboards use FontAwesome icons exclusively per shared UI guidance.

const CoursesSelectModelSingleton = new CoursesSelectModel();

type StatusMeta = {
  label: string;
  tone: 'neutral' | 'info' | 'success' | 'warning' | 'error';
  icon: IconDefinition;
};

const STATUS_META: Record<OverallStatus, StatusMeta> = {
  loading: { label: 'Loading', tone: 'info', icon: faSpinner },
  idle: { label: 'Wait', tone: 'neutral', icon: faClock },
  running: { label: 'Running', tone: 'info', icon: faSpinner },
  attention: { label: 'Attention', tone: 'warning', icon: faCircleExclamation },
  error: { label: 'Error', tone: 'error', icon: faTriangleExclamation },
  complete: { label: 'OK', tone: 'success', icon: faCircleCheck },
  empty: { label: 'No checks', tone: 'neutral', icon: faMinusCircle },
};

type PublishAllLevel = 'green' | 'warnings' | 'all';

type PublishControls = {
  publish: () => Promise<void>;
  // Runs the course's full Health check set server-side and stores the gate verdict.
  persist: () => Promise<void>;
  gateStatus: string;
  ready: boolean;
};

const PUBLISH_ALL_LABELS: Record<PublishAllLevel, string> = {
  green: 'Green only',
  warnings: 'Green + warnings',
  all: 'All courses (override red)',
};

const MultiCourseHealth: React.FC = () => {
  const cacheKey = getCacheKey(IRefIds.courses, 0);
  const allCourses = useCacheData(cacheKey, CoursesSelectModelSingleton.get.bind(CoursesSelectModelSingleton));
  // Health only deals with publishable courses: publish lesson 0 means nothing to check or publish.
  const courseOptions = React.useMemo(
    () => (allCourses ? allCourses.filter((course) => (course.publishLessonPosition ?? 0) > 0) : null),
    [allCourses],
  );

  const [selectedCourses, setSelectedCourses] = React.useState<ICourse[]>([]);
  const [activeCourses, setActiveCourses] = React.useState<ICourse[]>([]);
  const [readyCourses, setReadyCourses] = React.useState<Record<number, boolean>>({});
  const [runGeneration, setRunGeneration] = React.useState(0);
  const [currentRunner, setCurrentRunner] = React.useState<number | null>(null);
  const [isCourseSelectOpen, setCourseSelectOpen] = React.useState(false);
  const runQueueRef = React.useRef<number[]>([]);
  const publishControlsRef = React.useRef<Record<number, PublishControls>>({});
  const [publishMenuAnchor, setPublishMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [publishAllRunning, setPublishAllRunning] = React.useState(false);
  const [verifyAllRunning, setVerifyAllRunning] = React.useState(false);

  const handleCourseSelection = React.useCallback((value: ICourse[]) => {
    setSelectedCourses(value);
  }, []);

  const handleCourseDropdownOpen = React.useCallback(() => {
    setCourseSelectOpen(true);
  }, []);

  const handleCourseDropdownClose = React.useCallback((_event: React.ChangeEvent<unknown>, reason?: string) => {
    if (reason === 'select-option' || reason === 'remove-option') {
      setCourseSelectOpen(true);
      return;
    }
    setCourseSelectOpen(false);
  }, []);

  const handleApplySelection = React.useCallback(() => {
    setActiveCourses(selectedCourses);
    setReadyCourses({});
    runQueueRef.current = [];
    setCurrentRunner(null);
    setRunGeneration(0);
    setCourseSelectOpen(false);
  }, [selectedCourses]);

  const handleReadyChange = React.useCallback((courseId: number, ready: boolean) => {
    setReadyCourses((prev) => ({ ...prev, [courseId]: ready }));
  }, []);

  const handleRunAll = React.useCallback(() => {
    const queue = activeCourses.map((course) => course.id).filter((courseId) => readyCourses[courseId]);
    if (queue.length === 0) {
      return;
    }
    runQueueRef.current = queue;
    setRunGeneration((token) => token + 1);
    setCurrentRunner(queue[0]);
  }, [activeCourses, readyCourses]);

  const handleCourseFinished = React.useCallback((courseId: number) => {
    const queue = runQueueRef.current;
    const index = queue.indexOf(courseId);
    if (index === -1) {
      return;
    }
    const nextCourseId = queue[index + 1] ?? null;
    if (nextCourseId) {
      setCurrentRunner(nextCourseId);
      setRunGeneration((token) => token + 1);
    } else {
      runQueueRef.current = [];
      setCurrentRunner(null);
    }
  }, []);

  const handlePublishControls = React.useCallback((courseId: number, controls: PublishControls | null) => {
    if (controls === null) {
      delete publishControlsRef.current[courseId];
    } else {
      publishControlsRef.current[courseId] = controls;
    }
  }, []);

  const eligibleForLevel = React.useCallback((controls: PublishControls | undefined, level: PublishAllLevel) => {
    if (!controls || !controls.ready) {
      return false;
    }
    if (level === 'all') {
      return true;
    }
    if (level === 'warnings') {
      return controls.gateStatus === 'green' || controls.gateStatus === 'warning';
    }
    return controls.gateStatus === 'green';
  }, []);

  const handlePublishAll = React.useCallback(
    async (level: PublishAllLevel) => {
      setPublishMenuAnchor(null);
      setPublishAllRunning(true);
      try {
        // Sequential on purpose: publishing is heavy server work, one course at a time.
        for (const course of activeCourses) {
          const controls = publishControlsRef.current[course.id];
          if (!eligibleForLevel(controls, level)) {
            continue;
          }
          await controls!.publish();
        }
      } finally {
        setPublishAllRunning(false);
      }
    },
    [activeCourses, eligibleForLevel],
  );

  const handleVerifyAll = React.useCallback(async () => {
    setVerifyAllRunning(true);
    try {
      // Sequential: each persist-run is a full server-side check sweep of the course.
      for (const course of activeCourses) {
        const controls = publishControlsRef.current[course.id];
        if (!controls) {
          continue;
        }
        await controls.persist();
      }
    } finally {
      setVerifyAllRunning(false);
    }
  }, [activeCourses]);

  const levelCount = React.useCallback(
    (level: PublishAllLevel) =>
      activeCourses.filter((course) => eligibleForLevel(publishControlsRef.current[course.id], level)).length,
    [activeCourses, eligibleForLevel],
  );

  const isLoadingCourses = courseOptions === null;
  const hasActiveCourses = activeCourses.length > 0;
  const anyCourseReady = activeCourses.some((course) => readyCourses[course.id]);

  return (
    <ContentWrapper>
      <PanelSelectionWrapper>
        <CourseSelectInput
          options={courseOptions}
          onChange={handleCourseSelection}
          value={selectedCourses}
          open={isCourseSelectOpen}
          onOpen={handleCourseDropdownOpen}
          onClose={handleCourseDropdownClose}
        />
        <PanelSelectionActions>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={handleApplySelection}
            disabled={selectedCourses.length === 0}
          >
            Load checks
          </Button>
          <Button variant="outlined" size="small" onClick={handleRunAll} disabled={!anyCourseReady}>
            Run all checks
          </Button>
          <Button
            variant="outlined"
            size="small"
            disabled={!hasActiveCourses || verifyAllRunning}
            onClick={() => void handleVerifyAll()}
          >
            {verifyAllRunning ? 'Verifying…' : 'Verify all'}
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            disabled={!hasActiveCourses || publishAllRunning || verifyAllRunning}
            onClick={(event) => setPublishMenuAnchor(event.currentTarget)}
          >
            {publishAllRunning ? 'Publishing…' : 'Publish all'}&nbsp;
            <FontAwesomeIcon icon={faCaretDown} />
          </Button>
          <Menu
            anchorEl={publishMenuAnchor}
            open={publishMenuAnchor !== null}
            onClose={() => setPublishMenuAnchor(null)}
            getContentAnchorEl={null}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {(['green', 'warnings', 'all'] as PublishAllLevel[]).map((level) => (
              <MenuItem
                key={level}
                dense
                disabled={levelCount(level) === 0}
                onClick={() => void handlePublishAll(level)}
              >
                <LevelDot $level={level} />
                {PUBLISH_ALL_LABELS[level]}&nbsp;<LevelCount>({levelCount(level)})</LevelCount>
              </MenuItem>
            ))}
          </Menu>
        </PanelSelectionActions>
      </PanelSelectionWrapper>

      {isLoadingCourses && (
        <PanelPlaceholder>
          <PlaceholderIcon icon={faSpinner} spin />
          <PanelPlaceholderText>Loading courses…</PanelPlaceholderText>
        </PanelPlaceholder>
      )}

      {!isLoadingCourses && !hasActiveCourses && (
        <PanelPlaceholder>
          <PanelPlaceholderText>Select one or more courses and click “Load checks”.</PanelPlaceholderText>
        </PanelPlaceholder>
      )}

      {activeCourses.map((course) => (
        <CoursePanel
          key={course.id}
          course={course}
          runGeneration={runGeneration}
          currentRunner={currentRunner}
          onReadyChange={handleReadyChange}
          onCourseFinished={handleCourseFinished}
          onPublishControls={handlePublishControls}
        />
      ))}
    </ContentWrapper>
  );
};
type CoursePanelProps = {
  course: ICourse;
  runGeneration: number;
  currentRunner: number | null;
  onReadyChange: (courseId: number, ready: boolean) => void;
  onCourseFinished: (courseId: number) => void;
  onPublishControls: (courseId: number, controls: PublishControls | null) => void;
};

const CoursePanel: React.FC<CoursePanelProps> = ({
  course,
  runGeneration,
  currentRunner,
  onReadyChange,
  onCourseFinished,
  onPublishControls,
}) => {
  const {
    groups,
    loading,
    error,
    summaryText,
    totalChecks,
    hasStarted,
    allCompleted,
    checksNeedingRetryCount,
    checkStates,
    startChecks,
    retryAllChecks,
    retryIncompleteChecks,
    retryCheck,
    reloadChecks,
    overallStatus,
    isRunning,
  } = useHealthChecks(course.id);

  const publishGate = usePublishGate(course.id);
  const coursePublish = useCoursePublish(course.id);
  const publishSummary = coursePublish.summary;

  React.useEffect(() => {
    onPublishControls(course.id, {
      publish: coursePublish.publish,
      persist: publishGate.persistRun,
      gateStatus: publishGate.gate?.status ?? 'no-run',
      ready: coursePublish.model.resources.length > 0 && publishSummary.state !== 'publishing',
    });
    return () => {
      onPublishControls(course.id, null);
    };
  }, [
    course.id,
    onPublishControls,
    coursePublish.publish,
    coursePublish.model.resources.length,
    publishGate.gate,
    publishGate.persistRun,
    publishSummary.state,
  ]);

  const hasChecks = groups.length > 0;
  const ready = !loading && !error && hasChecks;
  const lastRunGenerationRef = React.useRef(0);
  const completionGenerationRef = React.useRef(0);

  React.useEffect(() => {
    onReadyChange(course.id, ready);
    return () => {
      onReadyChange(course.id, false);
    };
  }, [course.id, onReadyChange, ready]);

  React.useEffect(() => {
    if (!ready) {
      return;
    }
    if (currentRunner === course.id && runGeneration > lastRunGenerationRef.current) {
      startChecks();
      lastRunGenerationRef.current = runGeneration;
    }
  }, [ready, currentRunner, runGeneration, course.id, startChecks]);

  React.useEffect(() => {
    if (runGeneration === 0) {
      return;
    }
    if (currentRunner !== course.id) {
      return;
    }
    if (!hasChecks || !hasStarted || !allCompleted) {
      return;
    }
    if (completionGenerationRef.current >= runGeneration) {
      return;
    }
    completionGenerationRef.current = runGeneration;
    onCourseFinished(course.id);
  }, [runGeneration, currentRunner, course.id, hasChecks, hasStarted, allCompleted, onCourseFinished]);

  // Once the checks have finished, the header reflects the gate verdict rather than the
  // check-level summary: a red gate is an error even if every check loaded fine.
  const headerStatus: OverallStatus = (() => {
    const checksSettled = overallStatus === 'complete' || overallStatus === 'attention';
    if (!checksSettled || !publishGate.gate) {
      return overallStatus;
    }
    switch (publishGate.gate.status) {
      case 'red':
        return 'error';
      case 'warning':
        return 'attention';
      case 'green':
        return 'complete';
      default:
        return overallStatus;
    }
  })();
  const statusMeta = STATUS_META[headerStatus];
  const showSpinner = overallStatus === 'loading' || isRunning;
  const iconTone: OverallStatus = showSpinner ? 'running' : headerStatus;

  return (
    <AccordionStyled defaultExpanded={false} square>
      <AccordionSummary expandIcon={<ExpandIcon icon={faChevronDown} />}>
        <PanelHeader>
          <PanelInfo>
            <PanelTitleRow>
              <StatusIcon icon={showSpinner ? faSpinner : statusMeta.icon} $tone={iconTone} spin={showSpinner} />
              <PanelTitle>{course.name}</PanelTitle>
            </PanelTitleRow>
            <SummarySnippet>{summaryText}</SummarySnippet>
            {publishSummary.state !== 'idle' && (
              <PublishSnippet>
                <SnippetBar
                  percent={publishSummary.percent}
                  tone={
                    publishSummary.state === 'failed' ? 'error' : publishSummary.state === 'done' ? 'success' : 'active'
                  }
                />
                <SnippetText
                  $tone={
                    publishSummary.state === 'failed'
                      ? '#b91c1c'
                      : publishSummary.state === 'done'
                      ? '#15803d'
                      : '#4b5563'
                  }
                >
                  {publishSummary.state === 'publishing'
                    ? `Publishing ${publishSummary.currentName ?? '…'}${
                        publishSummary.currentTotal
                          ? ` ${publishSummary.currentDone}/${publishSummary.currentTotal}`
                          : ''
                      } · ${publishSummary.doneResources}/${publishSummary.totalResources} resources`
                    : publishSummary.state === 'done'
                    ? `Published · ${publishSummary.totalResources} resources`
                    : `Publish: ${publishSummary.failedResources} failed`}
                </SnippetText>
              </PublishSnippet>
            )}
          </PanelInfo>
        </PanelHeader>
      </AccordionSummary>
      <AccordionDetails>
        {loading ? (
          <PanelPlaceholder>
            <PlaceholderIcon icon={faSpinner} spin />
            <PanelPlaceholderText>Loading health checks…</PanelPlaceholderText>
          </PanelPlaceholder>
        ) : error ? (
          <ErrorWrapper>
            <ErrorMessage>{error}</ErrorMessage>
            <Button variant="contained" size="small" onClick={reloadChecks}>
              Retry
            </Button>
          </ErrorWrapper>
        ) : (
          <PanelContent>
            <PublishGateView
              gate={publishGate.gate}
              loading={publishGate.loading}
              persisting={publishGate.persisting}
              onRefresh={publishGate.refresh}
              onPersist={publishGate.persistRun}
            />
            <CoursePublish coursePublish={coursePublish} gate={publishGate.gate} gateLoading={publishGate.loading} />
            <HealthCourseContent
              groups={groups}
              summaryText={summaryText}
              totalChecks={totalChecks}
              hasStarted={hasStarted}
              allCompleted={allCompleted}
              checksNeedingRetryCount={checksNeedingRetryCount}
              checkStates={checkStates}
              onStart={startChecks}
              onRetryAll={retryAllChecks}
              onRetryIncomplete={retryIncompleteChecks}
              onRetryCheck={retryCheck}
            />
          </PanelContent>
        )}
      </AccordionDetails>
    </AccordionStyled>
  );
};
const ExpandIcon = styled(FontAwesomeIcon)`
  font-size: 14px;
  color: #4b5563;
`;

const AccordionStyled = styled(Accordion)`
  && {
    background-color: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    box-shadow: none !important;
    margin-bottom: 16px;
  }

  &&::before {
    display: none;
  }

  &&.Mui-expanded {
    margin: 0 0 16px 0;
  }
`;

const PanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
`;

const PanelTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatusIcon = styled(FontAwesomeIcon)<{ $tone: OverallStatus }>`
  font-size: 16px;
  color: ${({ $tone }) =>
    $tone === 'loading' || $tone === 'running'
      ? '#1d4ed8'
      : $tone === 'attention'
      ? '#f59e0b'
      : $tone === 'error'
      ? '#b91c1c'
      : $tone === 'complete'
      ? '#15803d'
      : $tone === 'empty'
      ? '#374151'
      : '#4b5563'};
`;

const PanelInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const SummarySnippet = styled.span`
  font-size: 13px;
  color: #4b5563;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PanelContent = styled.div`
  width: 100%;
`;

export default MultiCourseHealth;

const CourseSelectInput = styled(HealthCourseSelect)`
  flex: 1 1 100%;
  min-width: 0;
`;

const PanelSelectionWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
  align-items: center;
  width: 100%;
`;

const PanelSelectionActions = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;
  margin-left: auto;
`;

const PanelPlaceholder = styled.div`
  padding: 24px;
  border: 1px dashed #d1d5db;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #4b5563;
  background-color: #f9fafb;
`;

const PlaceholderIcon = styled(FontAwesomeIcon)`
  font-size: 18px;
  color: #2563eb;
`;

const PanelPlaceholderText = styled.span`
  font-size: 14px;
`;

const PublishSnippet = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 420px;
`;

const SnippetBar = styled(PublishProgressBar)`
  flex: 0 0 140px;
`;

const SnippetText = styled.span<{ $tone: string }>`
  font-size: 12px;
  color: ${({ $tone }) => $tone};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-variant-numeric: tabular-nums;
`;

const LevelDot = styled.span<{ $level: PublishAllLevel }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 8px;
  background-color: ${({ $level }) => ($level === 'green' ? '#15803d' : $level === 'warnings' ? '#f59e0b' : '#b91c1c')};
`;

const LevelCount = styled.span`
  color: #6b7280;
  font-size: 12px;
`;

const PanelTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
`;
